/**
 * Boleta o factura, y los datos tributarios que la factura exige (CHK-04).
 *
 * Los campos de empresa aparecen solo al elegir factura. No es solo comodidad:
 * la mayoria compra con boleta, y campos de empresa siempre visibles hacen
 * dudar a quien no tiene ninguno. Con factura va tambien la direccion
 * tributaria (`CamposDireccionFacturacion`).
 *
 * Con boleta se pide el RUT personal, y es OPCIONAL: quien lo da recibe la boleta a su nombre, y quien no, a
 * consumidor final. Obligarlo haria abandonar la compra a quien no quiere
 * darlo, que es justo lo que la regla de arriba busca evitar. Es un campo
 * aparte del RUT de la empresa, y no el mismo: si fuera uno solo, pasar de
 * boleta a factura dejaria el RUT personal escrito como si fuera el de la
 * empresa, y es facil no notarlo.
 *
 * El RUT se valida con su digito verificador en el navegador. El documento se
 * emite a mano DESPUES de cobrar, asi que un RUT mal escrito se descubriria con
 * la venta ya hecha; el modulo 11 atrapa el tipeo antes de que eso pase.
 *
 * El sitio NUNCA emite el documento: lo emite el negocio en el SII y lo sube
 * desde el panel. Lo que se elige aca es cual se pide.
 */
import type { OpcionesDespacho } from '@bandu/compartido';

import type { DatosCheckout, ErroresCheckout } from '../../utilidades/checkout';
import { formatearRut } from '../../utilidades/rut';
import { Campo } from './Campo';
import { CamposDireccionFacturacion } from './CamposDireccionFacturacion';

interface Props {
  datos: DatosCheckout;
  errores: ErroresCheckout;
  opciones: OpcionesDespacho | null;
  alCambiar: (cambio: Partial<DatosCheckout>) => void;
  alSalirDelCampo: (campo: keyof DatosCheckout) => void;
}

export function PanelDocumento({ datos, errores, opciones, alCambiar, alSalirDelCampo }: Props) {
  return (
    <section className="panel" aria-labelledby="ck-documento">
      <div className="panel-cab">
        <h2 id="ck-documento">Documento</h2>
      </div>

      <div className="panel-cuerpo">
        <fieldset>
          <legend className="solo-lectores">Que documento necesitas</legend>

          <label className={`opcion-envio${datos.tipoDocumento === 'boleta' ? ' sel' : ''}`}>
            <input
              type="radio"
              name="documento"
              value="boleta"
              checked={datos.tipoDocumento === 'boleta'}
              onChange={() => alCambiar({ tipoDocumento: 'boleta' })}
            />
            <span className="opcion-texto">
              <span className="t">Boleta</span>
              <span className="d">Para compras personales. Solo te pedimos tu RUT, y es opcional.</span>
            </span>
          </label>

          <label className={`opcion-envio${datos.tipoDocumento === 'factura' ? ' sel' : ''}`}>
            <input
              type="radio"
              name="documento"
              value="factura"
              checked={datos.tipoDocumento === 'factura'}
              onChange={() => alCambiar({ tipoDocumento: 'factura' })}
            />
            <span className="opcion-texto">
              <span className="t">Factura</span>
              <span className="d">
                Para empresas con giro. Necesitamos RUT, razon social, giro y direccion.
              </span>
            </span>
          </label>
        </fieldset>

        {datos.tipoDocumento === 'boleta' ? (
          <div className="detalle-entrega">
            <Campo
              id="ck-rut-comprador"
              etiqueta="Tu RUT (opcional)"
              error={errores.rutComprador}
              ayuda="Para que la boleta quede a tu nombre. Si lo dejas en blanco, se emite a consumidor final."
            >
              {(atributos) => (
                <input
                  {...atributos}
                  type="text"
                  inputMode="text"
                  placeholder="12.345.678-5"
                  value={datos.rutComprador}
                  onChange={(evento) => alCambiar({ rutComprador: evento.target.value })}
                  // Igual que el de la empresa: se formatea al salir, no en
                  // cada tecla. Vacio queda vacio.
                  onBlur={() => {
                    if (datos.rutComprador.trim().length > 0) {
                      alCambiar({ rutComprador: formatearRut(datos.rutComprador) });
                    }
                    alSalirDelCampo('rutComprador');
                  }}
                />
              )}
            </Campo>
          </div>
        ) : null}

        {datos.tipoDocumento === 'factura' ? (
          <div className="detalle-entrega">
            <div className="fila-campos">
              <Campo id="ck-rut" etiqueta="RUT de la empresa" error={errores.rut}>
                {(atributos) => (
                  <input
                    {...atributos}
                    type="text"
                    inputMode="text"
                    placeholder="12.345.678-5"
                    value={datos.rut}
                    onChange={(evento) => alCambiar({ rut: evento.target.value })}
                    // Se formatea al salir y no en cada tecla: reordenar los
                    // puntos mientras alguien escribe le mueve el cursor.
                    onBlur={() => {
                      alCambiar({ rut: formatearRut(datos.rut) });
                      alSalirDelCampo('rut');
                    }}
                  />
                )}
              </Campo>

              <Campo id="ck-razon" etiqueta="Razon social" error={errores.razonSocial}>
                {(atributos) => (
                  <input
                    {...atributos}
                    type="text"
                    autoComplete="organization"
                    value={datos.razonSocial}
                    onChange={(evento) => alCambiar({ razonSocial: evento.target.value })}
                    onBlur={() => alSalirDelCampo('razonSocial')}
                  />
                )}
              </Campo>
            </div>

            <Campo
              id="ck-giro"
              etiqueta="Giro"
              error={errores.giro}
              ayuda="La actividad economica con la que la empresa esta inscrita en el SII."
            >
              {(atributos) => (
                <input
                  {...atributos}
                  type="text"
                  value={datos.giro}
                  onChange={(evento) => alCambiar({ giro: evento.target.value })}
                  onBlur={() => alSalirDelCampo('giro')}
                />
              )}
            </Campo>

            <CamposDireccionFacturacion
              datos={datos}
              errores={errores}
              opciones={opciones}
              alCambiar={alCambiar}
              alSalirDelCampo={alSalirDelCampo}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
}
